import {
  creditosPorResolucion,
  type ResolucionImagen,
} from './resolucionImagen';

/**
 * Cuántos créditos va a cobrar una generación de imágenes.
 *
 * **Es un espejo de la fórmula del backend** — `expectedDeliveredCount` en
 * `atlas-backend/src/services/ads/composer.ts`, la misma cuenta que usa
 * `/v1/ads/generate` para su gate de saldo y el worker para debitar. Se cobra por
 * imagen ENTREGADA, así que la cuenta no depende de cuántos productos (`variante_id`)
 * se manden, sino de las poses / vistas / plantillas.
 *
 * Para qué existe del lado del front: para poder frenar ANTES de pedir la generación y
 * mostrar el catálogo de planes, en vez de mandar el request y que el backend lo
 * rechace con un error (ADR-2026-08-03-creditos-insuficientes-muestran-planes).
 *
 * Si cambia la fórmula del backend, hay que cambiar ésta.
 */
export interface CostoGeneracionInput {
  /**
   * Resolución elegida: multiplica el costo de CADA imagen entregada (1K=1 · 2K=2 ·
   * 4K=3). Las intermedias de la multi-pose no entran en la cuenta porque nunca se
   * cobraron — se generan en 1K y con `active=false`.
   * Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k.
   */
  resolucion?: ResolucionImagen | null;
  /** `'modelo'` o `'producto'`. Cualquier otra cosa se trata como `'producto'`. */
  composicion?: string;
  /** Lo que viaja como `cantidad_imagenes`. */
  cantidadImagenes: number;
  /** Poses que se mandan (normales + custom), si es que se mandan. */
  poses?: string[];
  /** Vistas de producto que se mandan, si es que se mandan. */
  vistas?: string[];
  /** Plantillas: una generación por plantilla, y manda sobre todo lo demás. */
  templateIds?: string[];
}

export function costoDeGeneracion(p: CostoGeneracionInput): number {
  return imagenesEntregadas(p) * creditosPorResolucion(p.resolucion);
}

/** Cuántas imágenes va a ENTREGAR esta generación (sin mirar la resolución). */
function imagenesEntregadas(p: CostoGeneracionInput): number {
  if ((p.templateIds ?? []).length > 0) return p.templateIds!.length;
  const composicion = p.composicion === 'modelo' ? 'modelo' : 'producto';
  const poses = (p.poses ?? []).length;
  const vistas = (p.vistas ?? []).length;
  if (composicion === 'modelo' && poses > 1) return poses;
  if (composicion === 'producto' && vistas > 1) return vistas;
  return p.cantidadImagenes;
}

export default costoDeGeneracion;
